import { useEffect } from "react";
import {
  BRAND,
  DEFAULT_OG_IMAGE,
  SITE_URL,
  SUPPORTED_LOCALES,
  buildTitle,
} from "./seo.config";
import { SeoMeta } from "./seo.types";

/**
 * Marker attribute on every tag this module owns, so cleanup never touches
 * the static tags that ship in public/index.html.
 */
const OWNED = "data-seo";

const HARD_NOINDEX =
  "noindex, nofollow, noarchive, nosnippet, noimageindex" as const;

const head = () => document.head;

const upsertMeta = (
  attribute: "name" | "property",
  key: string,
  content: string | null
): void => {
  const selector = `meta[${attribute}="${key}"]`;
  let element = head().querySelector<HTMLMetaElement>(selector);

  if (content === null) {
    // Only remove tags we created; leave index.html defaults intact.
    if (element && element.hasAttribute(OWNED)) element.remove();
    return;
  }

  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, key);
    element.setAttribute(OWNED, "");
    head().appendChild(element);
  }
  element.setAttribute("content", content);
};

const upsertLink = (rel: string, href: string | null): void => {
  const selector = `link[rel="${rel}"][${OWNED}]`;
  let element = head().querySelector<HTMLLinkElement>(selector);

  if (href === null) {
    if (element) element.remove();
    return;
  }

  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", rel);
    element.setAttribute(OWNED, "");
    head().appendChild(element);
  }
  element.setAttribute("href", href);
};

const setJsonLd = (blocks: Array<Record<string, unknown>> | undefined): void => {
  head()
    .querySelectorAll(`script[type="application/ld+json"][${OWNED}]`)
    .forEach((node) => node.remove());

  if (!blocks || blocks.length === 0) return;

  blocks.forEach((block) => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute(OWNED, "");
    // JSON.stringify escapes nothing dangerous here: all values are literals
    // from seo.config.ts, never user input. Guard "</" defensively anyway.
    script.textContent = JSON.stringify(block).replace(/<\//g, "<\\/");
    head().appendChild(script);
  });
};

const toAbsolute = (pathOrUrl: string): string =>
  /^https?:\/\//i.test(pathOrUrl)
    ? pathOrUrl
    : `${SITE_URL}${pathOrUrl.startsWith("/") ? "" : "/"}${pathOrUrl}`;

/**
 * Applies `meta` to document.head and keeps it in sync.
 *
 * @param meta      resolved metadata for the current route
 * @param pathname  current location pathname (query/hash deliberately excluded)
 * @param language  active i18next language code
 */
export const useSeo = (
  meta: SeoMeta,
  pathname: string,
  language: string
): void => {
  useEffect(() => {
    const {
      title,
      description,
      robots,
      canonicalPath,
      ogType = "website",
      ogImage = DEFAULT_OG_IMAGE,
      jsonLd,
      sensitive = false,
    } = meta;

    const resolvedTitle = buildTitle(title);
    // Default to noindex: an app route should have to opt IN to being indexed.
    const resolvedRobots = sensitive
      ? HARD_NOINDEX
      : robots ?? "noindex, follow";
    const isIndexable = !resolvedRobots.startsWith("noindex");

    /* Title ---------------------------------------------------------------- */
    document.title = resolvedTitle;

    /* <html lang> - correct language is an accessibility and snippet signal - */
    const shortLang = (language || "en").split("-")[0];
    document.documentElement.lang = shortLang;

    /* Robots --------------------------------------------------------------- */
    upsertMeta("name", "robots", resolvedRobots);
    upsertMeta("name", "googlebot", resolvedRobots);

    /* Description ---------------------------------------------------------- */
    // A description on a noindex page is never shown; skip it.
    upsertMeta("name", "description", isIndexable ? description ?? null : null);

    /* Canonical ------------------------------------------------------------ */
    // Never emit a canonical for a token-bearing URL, and never include the
    // query string - "?token=..." must not be advertised as a canonical URL.
    if (sensitive) {
      upsertLink("canonical", null);
    } else {
      const path = canonicalPath ?? pathname;
      upsertLink("canonical", toAbsolute(path === "" ? "/" : path));
    }

    /* Open Graph / Twitter ------------------------------------------------- */
    // Worth maintaining even on noindex pages: this is what renders when a
    // teammate pastes a link into Slack, Teams, WhatsApp or LinkedIn.
    const absoluteImage = toAbsolute(ogImage);

    upsertMeta("property", "og:site_name", BRAND);
    upsertMeta("property", "og:type", ogType);
    upsertMeta("property", "og:title", resolvedTitle);
    upsertMeta("property", "og:description", description ?? null);
    upsertMeta("property", "og:image", absoluteImage);
    upsertMeta("property", "og:image:width", "1200");
    upsertMeta("property", "og:image:height", "630");
    upsertMeta("property", "og:image:alt", `${BRAND} — ${title}`);
    upsertMeta("property", "og:locale", SUPPORTED_LOCALES[shortLang] || "en_US");
    upsertMeta(
      "property",
      "og:url",
      sensitive ? toAbsolute("/") : toAbsolute(canonicalPath ?? pathname)
    );

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", resolvedTitle);
    upsertMeta("name", "twitter:description", description ?? null);
    upsertMeta("name", "twitter:image", absoluteImage);

    /* Structured data ------------------------------------------------------ */
    setJsonLd(isIndexable ? jsonLd : undefined);

    /* Referrer hardening on token pages ------------------------------------ */
    // Prevents the invite/reset token leaking to third parties via Referer.
    upsertMeta("name", "referrer", sensitive ? "no-referrer" : null);
  }, [meta, pathname, language]);
};

export default useSeo;
